import React from 'react'
import Dropzone, { Accept } from 'react-dropzone'
import {
    Box,
    Typography,
    makeStyles,
    Theme,
    IconButton,
} from '@material-ui/core'
import {
    CloudUpload as Upload,
    CheckCircle,
    Delete as DeleteIcon,
} from '@material-ui/icons'

interface SpreadsheetUploadCardProps {
    onDrop: (
        acceptedFiles: File[],
        rejectedFiles?: Array<{
            file: File
            errors: Array<{ code: string; message: string }>
        }>,
    ) => void
    uploadedFile: File | null
    onRemove?: () => void
    fileTypes?: Accept
    multipleFiles?: boolean
    title?: string
    description?: string
    className?: string
    selectedClassName?: string
    titleClassName?: string
    descriptionClassName?: string
}

const useStyles = makeStyles((theme: Theme) => ({
    dropZoneContainer: {
        width: '100%',
        height: '100%',
    },
    card: {
        border: '2px solid #e2e8f0',
        borderRadius: '8px',
        padding: theme.spacing(2),
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        width: '100%',
        height: '100%',
        '&:hover': {
            borderColor: '#3b82f6',
            backgroundColor: 'rgba(59, 130, 246, 0.04)',
        },
    },
    cardSelected: {
        borderColor: '#3b82f6',
        backgroundColor: 'rgba(59, 130, 246, 0.08)',
    },
    title: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        fontSize: '1rem',
        fontWeight: 600,
        color: '#1f2937',
        marginBottom: theme.spacing(0.5),
        width: '100%',
    },
    description: {
        color: '#6b7280',
        fontSize: '0.875rem',
    },
    fileName: {
        color: '#10b981',
        fontSize: '0.875rem',
        marginTop: theme.spacing(0.5),
        fontWeight: 500,
    },
}))

export const SpreadsheetUploadCard = (props: SpreadsheetUploadCardProps) => {
    const {
        onDrop,
        uploadedFile,
        onRemove,
        fileTypes,
        multipleFiles = false,
        title = 'CSV Upload',
        description = 'Upload your existing prospect list',
        className,
        selectedClassName,
        titleClassName,
        descriptionClassName,
    } = props

    const classes = useStyles()

    const defaultFileTypes: Accept = {
        'text/csv': ['.csv'],
        'application/vnd.ms-excel': ['.xls'],
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': [
            '.xlsx',
        ],
    }

    return (
        <Dropzone
            onDrop={(acceptedFiles, rejectedFiles) => {
                onDrop(acceptedFiles, rejectedFiles)
            }}
            accept={fileTypes || defaultFileTypes}
            multiple={multipleFiles}
            useFsAccessApi={false}
        >
            {({ getRootProps, getInputProps, isDragActive }) => (
                <div {...getRootProps()} className={classes.dropZoneContainer}>
                    <input {...getInputProps()} type="file" />
                    <Box
                        className={`${className || classes.card} ${
                            uploadedFile
                                ? selectedClassName || classes.cardSelected
                                : ''
                        } ${isDragActive ? classes.cardSelected : ''}`}
                    >
                        <Typography className={titleClassName || classes.title}>
                            <Upload
                                style={{
                                    color: '#3b82f6',
                                    fontSize: 20,
                                }}
                            />
                            Spreadsheet Upload
                            {uploadedFile && (
                                <CheckCircle
                                    style={{
                                        color: '#10b981',
                                        fontSize: 18,
                                        marginLeft: 'auto',
                                    }}
                                />
                            )}
                        </Typography>
                        <Typography
                            className={
                                descriptionClassName || classes.description
                            }
                        >
                            {description}
                        </Typography>
                        {uploadedFile && (
                            <Box
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    marginTop: 8,
                                }}
                            >
                                <Typography className={classes.fileName}>
                                    {uploadedFile.name}
                                </Typography>
                                {onRemove && (
                                    <IconButton
                                        size="small"
                                        onClick={(e) => {
                                            e.stopPropagation()
                                            onRemove()
                                        }}
                                        style={{
                                            padding: 4,
                                            color: '#ef4444',
                                        }}
                                    >
                                        <DeleteIcon fontSize="small" />
                                    </IconButton>
                                )}
                            </Box>
                        )}
                        {isDragActive && !uploadedFile && (
                            <Typography className={classes.fileName}>
                                Drop your spreadsheet here...
                            </Typography>
                        )}
                    </Box>
                </div>
            )}
        </Dropzone>
    )
}
